import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _typeof from "@babel/runtime/helpers/typeof";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
import _toString from 'lodash/toString';
import { v5 as uuidv5 } from 'uuid';
import { HANDLED_PROPS, DEFAULT_SCRIPT_LOAD_STRATEGY, ID_NAMESPACE, DEFAULT_TARGET_SELECTOR } from './Script.constants';

/**
 * scriptCallbackCache shape:
 *
 * scriptKey -> {
 *  load: {
 *    callbacks: [function]
 *    event: Event
 *  }
 *  error: {
 *    callbacks: [function]
 *    event: Event
 *  }
 * }
 */
var scriptCallbackCache = {};
var scriptCache = new Set();
export function getRequestIdleCallback() {
  if ((typeof window === "undefined" ? "undefined" : _typeof(window)) === "undefined" || !("requestIdleCallback" in window)) {
    return function (callback) {
      return setTimeout(function () {
        var start = Date.now();
        callback({
          didTimeout: false,
          timeRemaining: function timeRemaining() {
            return Math.max(0, 50 - (Date.now() - start));
          }
        });
      }, 1);
    };
  }
  return window.requestIdleCallback;
}
export function getCancelIdleCallback() {
  if ((typeof window === "undefined" ? "undefined" : _typeof(window)) === "undefined" || !("cancelIdleCallback" in window)) {
    return function (id) {
      return clearTimeout(id);
    };
  }
  return window.cancelIdleCallback;
}
export function appendDomNode(domNode, targetSelector) {
  var targetNode = document.querySelector(targetSelector);
  if (_isNil(targetNode)) {
    // eslint-disable-next-line no-console
    console.error("".concat(targetSelector, " was not found in document. Node cannot be appended."));
  } else {
    targetNode.appendChild(domNode);
  }
}
export function resolveAttributes(scriptProps) {
  var attributes = {};
  Object.entries(scriptProps).forEach(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
      key = _ref2[0],
      value = _ref2[1];
    if (HANDLED_PROPS.has(key)) {
      return;
    }
    attributes[key] = value;
  });
  return attributes;
}
export function resolveInlineScript(scriptProps) {
  var _ref3 = scriptProps || {},
    dangerouslySetInnerHTML = _ref3.dangerouslySetInnerHTML,
    _ref3$children = _ref3.children,
    children = _ref3$children === void 0 ? "" : _ref3$children;
  var _ref4 = dangerouslySetInnerHTML || {},
    _ref4$__html = _ref4.__html,
    dangerousHTML = _ref4$__html === void 0 ? "" : _ref4$__html;
  return _toString(dangerousHTML) || children;
}
function onEventCallback(_x, _x2, _x3) {
  return _onEventCallback.apply(this, arguments);
}
function _onEventCallback() {
  _onEventCallback = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2(event, scriptKey, eventName) {
    var cachedCallbacks, promises;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          cachedCallbacks = _get(scriptCallbackCache, scriptKey) || {};
          promises = _get(cachedCallbacks, "".concat(eventName, ".callbacks"), []).map(function (callback) {
            return callback(event);
          });
          _context2.next = 4;
          return Promise.all(promises);
        case 4:
          scriptCallbackCache[scriptKey] = _defineProperty({}, eventName, {
            event: event
          });
        case 5:
        case "end":
          return _context2.stop();
      }
    }, _callee2);
  }));
  return _onEventCallback.apply(this, arguments);
}
export function resolveCallbacks(givenCallbacks, scriptKey, cache) {
  /**
   * If a duplicate script is already loaded/errored, we replay load/error callbacks with the original event.
   * If it's not yet loaded/errored, keep track of callbacks so we can call load/error callbacks for each when the event occurs.
   */
  Object.keys(givenCallbacks).forEach(function (name) {
    var cachedCallbacks = _get(cache, scriptKey) || {};
    var _get2 = _get(cachedCallbacks, name, {}),
      _get2$callbacks = _get2.callbacks,
      callbacks = _get2$callbacks === void 0 ? [] : _get2$callbacks;
    callbacks.push(givenCallbacks[name]);
    if (_get(cachedCallbacks, "".concat(name, ".event"))) {
      givenCallbacks[name](_get(cachedCallbacks, "".concat(name, ".event")));
    } else {
      // eslint-disable-next-line no-param-reassign
      cache[scriptKey] = _objectSpread(_objectSpread({}, cachedCallbacks), {}, _defineProperty({}, name, {
        callbacks: callbacks
      }));
    }
  });
}
export function injectScript(scriptProps) {
  var _ref5 = scriptProps || {},
    id = _ref5.id,
    src = _ref5.src,
    _ref5$strategy = _ref5.strategy,
    strategy = _ref5$strategy === void 0 ? DEFAULT_SCRIPT_LOAD_STRATEGY : _ref5$strategy,
    onLoad = _ref5.onLoad,
    onError = _ref5.onError,
    _ref5$targetSelector = _ref5.targetSelector,
    targetSelector = _ref5$targetSelector === void 0 ? DEFAULT_TARGET_SELECTOR : _ref5$targetSelector;
  var inlineScript = resolveInlineScript(scriptProps);
  var scriptKey = id || uuidv5(src || inlineScript, ID_NAMESPACE);
  var callbackNames = ["load", "error"];
  var currentCallbacks = {
    load: onLoad,
    error: onError
  };
  if (!scriptKey) {
    return null;
  }
  resolveCallbacks(currentCallbacks, scriptKey, scriptCallbackCache);

  // Avoid injecting duplicate scripts into the DOM
  if (scriptCache.has(scriptKey)) {
    return null;
  }
  var attributes = resolveAttributes(scriptProps);
  var script = document.createElement("script");
  if (id) {
    script.id = id;
  }
  script.dataset.strategy = strategy;
  Object.entries(attributes).forEach(function (_ref6) {
    var _ref7 = _slicedToArray(_ref6, 2),
      key = _ref7[0],
      value = _ref7[1];
    script.setAttribute(key, value);
  });
  if (inlineScript) {
    script.textContent = inlineScript;
  }
  if (src) {
    script.src = src;
  }
  var wrappedCallbacks = {};

  // Add listeners on injected scripts so events are cached for use in de-duplicated script callbacks
  callbackNames.forEach(function (name) {
    var wrappedEventCallback = /*#__PURE__*/function () {
      var _ref8 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(event) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              return _context.abrupt("return", onEventCallback(event, scriptKey, name));
            case 1:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }));
      return function wrappedEventCallback(_x4) {
        return _ref8.apply(this, arguments);
      };
    }();
    script.addEventListener(name, wrappedEventCallback);
    wrappedCallbacks["".concat(name, "Callback")] = wrappedEventCallback;
  });
  scriptCache.add(scriptKey);
  appendDomNode(script, targetSelector);
  return {
    script: script,
    loadCallback: wrappedCallbacks.loadCallback,
    errorCallback: wrappedCallbacks.errorCallback,
    scriptKey: scriptKey
  };
}
export function removeInjectedScript(details) {
  var idleTimeoutId = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : undefined;
  var _ref9 = details || {},
    script = _ref9.script,
    loadCallback = _ref9.loadCallback,
    errorCallback = _ref9.errorCallback,
    scriptKey = _ref9.scriptKey;
  if (idleTimeoutId) {
    getCancelIdleCallback()(idleTimeoutId);
  }
  if (!script) {
    return;
  }
  if (loadCallback) {
    script.removeEventListener("load", loadCallback);
  }
  if (errorCallback) {
    script.removeEventListener("error", errorCallback);
  }
  script.remove();
  scriptCache["delete"](scriptKey);
}